import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  LayoutDashboard,
  CreditCard,
  FileText,
  FolderOpen,
  LogOut,
  Users,
  Settings,
  Bell,
  Globe,
  Shield
} from 'lucide-react';

const ClientNav = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const role = (user?.role || '').toLowerCase();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  const isStaff = ['admin', 'developer', 'outreach'].includes(role);
  const canSeeWebsiteEditor = role === 'admin' || role === 'developer';
  const canManageUsers = role === 'admin';

  // Staff/admin nav
  const staffNavItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/clients', label: 'Clients', icon: Users },
    { path: '/admin/requests', label: 'Requests', icon: FileText },
    { path: '/admin/payments', label: 'Payments', icon: CreditCard },
    ...(canSeeWebsiteEditor ? [{ path: '/admin/website-editor', label: 'Website Editor', icon: Globe }] : []),
    ...(canManageUsers ? [{ path: '/admin/users', label: 'Users', icon: Shield }] : []),
  ];

  // Client nav
  const clientNavItems = [
    { path: '/client/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/client/plan', label: 'Plan', icon: Settings },
    { path: '/client/requests', label: 'Requests', icon: FileText },
    { path: '/client/invoices', label: 'Invoices', icon: CreditCard },
    { path: '/client/files', label: 'Files', icon: FolderOpen },
  ];

  const navItems = isStaff ? staffNavItems : clientNavItems;

  // Notifications button should only point to a real route if you have it.
  // You currently link to /admin/notifications for admin only.
  // We'll keep staff -> /admin/notifications, client -> /client/notifications
  const notificationsPath = isStaff ? '/admin/notifications' : '/client/notifications';

  return (
    <nav className="bg-slate-950 border-b border-slate-800 sticky top-0 z-50" data-testid="portal-nav">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="text-xl font-bold font-['Syne'] text-white">
            DH <span className="text-indigo-500">Home Networking</span>
          </div>

          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors ${
                    isActive(item.path)
                      ? 'bg-indigo-500/20 text-indigo-400'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          <div className="flex items-center space-x-4">
            <Link
              to={notificationsPath}
              className="text-slate-400 hover:text-white transition-colors relative"
              data-testid="notifications-btn"
            >
              <Bell size={20} />
            </Link>
            <span className="text-sm text-slate-400" data-testid="user-email">
              {user?.email}
            </span>
            <button
              onClick={handleLogout}
              className="text-slate-400 hover:text-white transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        <div className="md:hidden pb-3 flex overflow-x-auto space-x-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`px-3 py-2 rounded-lg flex items-center space-x-2 whitespace-nowrap ${
                  isActive(item.path)
                    ? 'bg-indigo-500/20 text-indigo-400'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon size={16} />
                <span className="text-sm">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default ClientNav;
